import React from 'react';

interface PreviewImage {
  id: number;
  thumbnail_path: string;
  file_path: string;
}

interface FolderCardProps {
  name: string;
  color: string;
  imageCount: number;
  videoCount?: number;
  previewImages: PreviewImage[];
  onClick: () => void;
}

const FolderCard: React.FC<FolderCardProps> = ({ name, color, imageCount, videoCount = 0, previewImages, onClick }) => {
  // Fill up to 4 slots
  const slots = Array(4).fill(null).map((_, index) => previewImages[index] || null);

  // Build count text: "X Images, Y Videos" (exclude either if 0)
  const countParts: string[] = [];
  if (imageCount > 0) {
    countParts.push(`${imageCount} Image${imageCount !== 1 ? 's' : ''}`);
  }
  if (videoCount > 0) {
    countParts.push(`${videoCount} Video${videoCount !== 1 ? 's' : ''}`);
  }
  const countText = countParts.length > 0 ? countParts.join(', ') : '0 Items';

  return (
    <div
      onClick={onClick}
      className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200 group hover:scale-105"
    >
      <div className="flex flex-col">
        {/* 2x2 Thumbnail Grid - touches edges */}
        <div className="aspect-square w-full grid grid-cols-2 gap-1 bg-gray-100">
          {slots.map((image, index) => (
            <div key={index} className="aspect-square bg-gray-200 flex items-center justify-center overflow-hidden">
              {image ? (
                <img
                  src={image.thumbnail_path || image.file_path}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
            </div>
          ))}
        </div>

        {/* Folder Name with Icon - reduced spacing */}
        <div className="flex items-center space-x-2 px-3 pt-2">
          <svg
            className="w-5 h-5 flex-shrink-0 group-hover:scale-110 transition-transform"
            fill="currentColor"
            viewBox="0 0 24 24"
            style={{ color }}
          >
            <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
          </svg>
          <h3 className="text-base font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors flex-1">
            {name}
          </h3>
        </div>

        {/* Item Count - reduced spacing to bottom */}
        <p className="text-sm text-gray-500 text-center px-3 pt-1 pb-2">
          {countText}
        </p>
      </div>
    </div>
  );
};

export default FolderCard;
